import { createSlice } from "@reduxjs/toolkit";
import { GetAllUsersData } from "../../../../types/redux";
import {
  GetAllSupplierData,
  NPSupplierInitialStatus,
  Pagination,
} from "../../../../types/redux/NP/supplier";
import {
  bulkCreatePOAsync,
  createBatchPaymentAsync,
  createPOAsync,
  createSupplierAsync,
  forwardRequestToStepAboveAsync,
  generateInvoiceAsync,
  getAllBatchmentPaymentAsync,
  getAllPOAsync,
  getAllSupplierAsync,
  getAllTowersByBatchPaymentIdAsync,
  getCompletedTowerBySupplierAsync,
  getCountSupplierAsync,
  getCountUserSupplierAsync,
  getGeneratePdfAsync,
  getSingleBatchmentPaymentAsync,
  getSinglePOAsync,
  getSinglePOForVendorAsync,
  getSingleSupplierAsync,
  getSupplierByIdAsync,
  getSupplierProfileAsync,
  getSupplierUserAsync,
  irExcelUploadAsync,
  markPaidPaymentAsync,
  rejectBatchPaymentAsync,
  updateBatchPaymentAsync,
  updatePOAsync,
  updateSupplierAsync,
} from "./supplierAPI";

const initialState: NPSupplierInitialStatus<
  GetAllSupplierData,
  GetAllUsersData
> = {
  loading: false,
  postLoading: false,
  updateLoading: false,
  deleteLoading: false,
  allSupplier: [],
  allPO: [],
  allCompletedTowerBySupplier: [],
  allBatchmentPayment: null,
  userSupplier: [],
  supplierProfile: null,
  supplierCount: 0,
  supplierUserCount: 0,
  singleSupplier: null,
  singlePO: null,
  getSingleBatchmentPayment: null,
  getAllTowersByBatchPaymentId: null,
  generateInvoice: null,
  getSinglePOForVendor: null,
  pagination: null,
  completedTowerPaginationResponse: null,
  getGeneratePdfData: null,
};

const supplierSlice = createSlice({
  name: "np-supplier",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // create user
    builder
      .addCase(createSupplierAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(createSupplierAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(createSupplierAsync.rejected, (state) => {
        state.postLoading = false;
      });
    // Update Supplier
    builder
      .addCase(updateSupplierAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(updateSupplierAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(updateSupplierAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // get all towers
    builder
      .addCase(getAllSupplierAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllSupplierAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allSupplier = action.payload;
      })
      .addCase(getAllSupplierAsync.rejected, (state) => {
        state.loading = false;
        state.allSupplier = [];
      });
    // get single supplier
    builder
      .addCase(getSupplierByIdAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSupplierByIdAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.singleSupplier = action.payload;
      })
      .addCase(getSupplierByIdAsync.rejected, (state) => {
        state.loading = false;
        state.singleSupplier = null;
      });
    // get supplier profile
    builder
      .addCase(getSupplierProfileAsync.pending, (state) => {
        state.viewLoading = true;
        state.supplierProfile = null;
      })
      .addCase(getSupplierProfileAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.supplierProfile = action.payload;
      })
      .addCase(getSupplierProfileAsync.rejected, (state) => {
        state.viewLoading = false;
        state.supplierProfile = null;
      });
    // get supplier count
    builder
      .addCase(getCountSupplierAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCountSupplierAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierCount = action.payload;
      })
      .addCase(getCountSupplierAsync.rejected, (state) => {
        state.loading = false;
        state.supplierCount = 0;
      });
    // get user suppliers
    builder
      .addCase(getSupplierUserAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSupplierUserAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.userSupplier = action.payload;
      })
      .addCase(getSupplierUserAsync.rejected, (state) => {
        state.loading = false;
        state.userSupplier = [];
      });
    // get supplier user count
    builder
      .addCase(getCountUserSupplierAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCountUserSupplierAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.supplierUserCount = action.payload;
      })
      .addCase(getCountUserSupplierAsync.rejected, (state) => {
        state.loading = false;
        state.supplierUserCount = 0;
      });

    // get single supplier
    builder
      .addCase(getSingleSupplierAsync.pending, (state) => {
        state.viewLoading = true;
      })
      .addCase(getSingleSupplierAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.singleSupplier = action.payload;
      })
      .addCase(getSingleSupplierAsync.rejected, (state) => {
        state.viewLoading = false;
        state.singleSupplier = null;
      });

    // create PO
    builder
      .addCase(createPOAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(createPOAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(createPOAsync.rejected, (state) => {
        state.postLoading = false;
      });
    // Get All PO
    builder
      .addCase(getAllPOAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPOAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allPO = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllPOAsync.rejected, (state) => {
        state.loading = false;
        state.allPO = [];
        state.pagination = {} as Pagination;
      });

    // get single PO
    builder
      .addCase(getSinglePOAsync.pending, (state) => {
        state.viewLoading = true;
      })
      .addCase(getSinglePOAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.singlePO = action.payload;
      })
      .addCase(getSinglePOAsync.rejected, (state) => {
        state.viewLoading = false;
        state.singlePO = null;
      });

    // Update PO
    builder
      .addCase(updatePOAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(updatePOAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(updatePOAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // create batch payment
    builder
      .addCase(createBatchPaymentAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(createBatchPaymentAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(createBatchPaymentAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // Update batch payment
    builder
      .addCase(updateBatchPaymentAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(updateBatchPaymentAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(updateBatchPaymentAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // Get all batchment payment
    builder
      .addCase(getAllBatchmentPaymentAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllBatchmentPaymentAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allBatchmentPayment = action.payload;
      })
      .addCase(getAllBatchmentPaymentAsync.rejected, (state) => {
        state.loading = false;
        state.allBatchmentPayment = null;
      });

    // get single batchment payment
    builder
      .addCase(getSingleBatchmentPaymentAsync.pending, (state) => {
        state.viewLoading = true;
      })
      .addCase(getSingleBatchmentPaymentAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.getSingleBatchmentPayment = action.payload;
      })
      .addCase(getSingleBatchmentPaymentAsync.rejected, (state) => {
        state.viewLoading = false;
        state.getSingleBatchmentPayment = null;
      });

    // Forward Request To Step Above
    builder
      .addCase(forwardRequestToStepAboveAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(forwardRequestToStepAboveAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(forwardRequestToStepAboveAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // Mark Payment as Paid
    builder
      .addCase(markPaidPaymentAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(markPaidPaymentAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(markPaidPaymentAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // Reject Batch Payment
    builder
      .addCase(rejectBatchPaymentAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(rejectBatchPaymentAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(rejectBatchPaymentAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // Get All Completed Tower By Supplier
    builder
      .addCase(getCompletedTowerBySupplierAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompletedTowerBySupplierAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.allCompletedTowerBySupplier = action.payload?.data;
        state.completedTowerPaginationResponse = action.payload;
      })
      .addCase(getCompletedTowerBySupplierAsync.rejected, (state) => {
        state.loading = false;
        state.allCompletedTowerBySupplier = [];
        state.completedTowerPaginationResponse = null;
      });

    // Get All Towers By Batch Payment Id
    builder
      .addCase(getAllTowersByBatchPaymentIdAsync.pending, (state) => {
        state.viewLoading = true;
      })
      .addCase(getAllTowersByBatchPaymentIdAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.getAllTowersByBatchPaymentId = action.payload;
      })
      .addCase(getAllTowersByBatchPaymentIdAsync.rejected, (state) => {
        state.viewLoading = false;
        state.getAllTowersByBatchPaymentId = null;
      });

    // Generate Invoice
    builder
      .addCase(generateInvoiceAsync.pending, (state) => {
        state.viewLoading = true;
      })
      .addCase(generateInvoiceAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.generateInvoice = action.payload;
      })
      .addCase(generateInvoiceAsync.rejected, (state) => {
        state.viewLoading = false;
        state.generateInvoice = null;
      });

    // Get Single PO For Vendor
    builder
      .addCase(getSinglePOForVendorAsync.pending, (state) => {
        state.viewLoading = true;
      })
      .addCase(getSinglePOForVendorAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.getSinglePOForVendor = action.payload;
      })
      .addCase(getSinglePOForVendorAsync.rejected, (state) => {
        state.viewLoading = false;
        state.getSinglePOForVendor = null;
      });

    // Bulk Create PO
    builder
      .addCase(bulkCreatePOAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(bulkCreatePOAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(bulkCreatePOAsync.rejected, (state) => {
        state.postLoading = false;
      });

    // builder
    //   .addCase(addBatchTaskAsync.pending, (state) => {
    //     state.postLoading = true;
    //   })
    //   .addCase(addBatchTaskAsync.fulfilled, (state) => {
    //     state.postLoading = false;
    //   })
    //   .addCase(addBatchTaskAsync.rejected, (state) => {
    //     state.postLoading = false;
    //   });

    // Get Generate PDF
    builder
      .addCase(getGeneratePdfAsync.pending, (state) => {
        state.viewLoading = true;
        state.getGeneratePdfData = null;
      })
      .addCase(getGeneratePdfAsync.fulfilled, (state, action) => {
        state.viewLoading = false;
        state.getGeneratePdfData = action.payload;
      })
      .addCase(getGeneratePdfAsync.rejected, (state) => {
        state.viewLoading = false;
        state.getGeneratePdfData = null;
      });

    // irExcelUpload
    builder
      .addCase(irExcelUploadAsync.pending, (state) => {
        state.postLoading = true;
      })
      .addCase(irExcelUploadAsync.fulfilled, (state) => {
        state.postLoading = false;
      })
      .addCase(irExcelUploadAsync.rejected, (state) => {
        state.postLoading = false;
      });
  },
});

export default supplierSlice;
